import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { toast } from 'sonner';
import { Loader2, Search, Download } from 'lucide-react';
import { Booking } from '../types/models';
import { CSVLink } from 'react-csv';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';

interface Attendee {
  bookingId: string;
  name: string;
  email: string;
  ticketTierName: string;
  purchasedAt: Date;
}

interface AttendeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string | null;
  eventName: string | null;
}

export const AttendeesModal: React.FC<AttendeesModalProps> = ({ isOpen, onClose, eventId, eventName }) => {
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && eventId) {
      fetchAttendees();
    } else {
      setAttendees([]);
      setSearchTerm('');
    }
  }, [isOpen, eventId]);

  const fetchAttendees = async () => {
    if (!eventId) return;

    setIsLoading(true);
    try {
      const bookingsQuery = query(collection(db, 'bookings'), where('eventId', '==', eventId));
      const querySnapshot = await getDocs(bookingsQuery);
      
      const attendeesList: Attendee[] = [];
      querySnapshot.forEach(doc => {
        const booking = doc.data() as Booking;
        if (booking.attendeeInfo && Array.isArray(booking.attendeeInfo)) {
          booking.attendeeInfo.forEach((att) => {
            attendeesList.push({
              bookingId: doc.id,
              name: att.name,
              email: att.email,
              ticketTierName: booking.ticketTierName,
              purchasedAt: booking.createdAt instanceof Timestamp 
                ? booking.createdAt.toDate() 
                : new Date(),
            });
          });
        }
      });
      
      setAttendees(attendeesList);
    } catch (error) {
      console.error('Error fetching attendees:', error);
      toast.error('Failed to load attendees.');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAttendees = useMemo(() => {
    return attendees.filter(attendee =>
      attendee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attendee.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [attendees, searchTerm]);

  const csvData = useMemo(() => {
    if (filteredAttendees.length === 0) return { headers: [], data: [] };
    
    const headers = [
      { label: 'Booking ID', key: 'bookingId' },
      { label: 'Attendee Name', key: 'name' },
      { label: 'Attendee Email', key: 'email' },
      { label: 'Ticket Type', key: 'ticketTierName' },
      { label: 'Purchased On (UTC)', key: 'purchasedAt' },
    ];
    
    const data = filteredAttendees.map(att => ({
      ...att,
      purchasedAt: format(att.purchasedAt, "yyyy-MM-dd HH:mm:ss")
    }));
    
    return { headers, data };
  }, [filteredAttendees]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Attendees for {eventName || 'Event'}</DialogTitle>
          <DialogDescription>
            Here is a list of all registered attendees for your event.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex justify-between items-center gap-4 my-4">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <CSVLink
            data={csvData.data}
            headers={csvData.headers}
            filename={`attendees_${eventName?.replace(/\s+/g, '_') ?? 'event'}_${format(new Date(), 'yyyyMMdd')}.csv`}
            className="flex-shrink-0"
          >
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </CSVLink>
        </div>

        <div className="max-h-[50vh] overflow-y-auto border rounded-md">
          <Table>
            <TableHeader className="sticky top-0 bg-gray-50">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Ticket</TableHead>
                <TableHead>Purchased On</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center h-24">
                    <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                  </TableCell>
                </TableRow>
              ) : filteredAttendees.length > 0 ? (
                filteredAttendees.map((attendee, index) => (
                  <TableRow key={`${attendee.bookingId}-${index}`}>
                    <TableCell className="font-medium">{attendee.name}</TableCell>
                    <TableCell>{attendee.email}</TableCell>
                    <TableCell>{attendee.ticketTierName}</TableCell>
                    <TableCell>{attendee.purchasedAt.toLocaleDateString()}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center h-24">
                    No attendees found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 